/**
 * Canonical seating geometry — SPEC v1.
 *
 * The single source of chair positions for both the canvas and the PDF
 * (`lib/pdf`). It began as a mirror of the server's `services/geometry.py`;
 * `geometry.test.ts` still pins the reference values the printed documents were
 * built on, so a change here is a change to every PDF — make it deliberately.
 *
 * Chair coordinates are never stored — they are a pure function of
 * (shape, seatCount, index). That is what makes chairs stable: moving, rotating,
 * adding or removing never nudges the others out of alignment.
 *
 * Coordinates are screen-style (+x right, +y down) with the origin at the table
 * centre. `facing` is degrees clockwise from "up", which is also what CSS
 * `rotate()` expects.
 */

import type { TableShape } from './types'

export const SEAT_RADIUS = 19
export const SEAT_PITCH = 46
export const SEAT_GAP = 14
export const MIN_ROUND_RADIUS = 62
export const MIN_SQUARE_SIDE = 120
export const SWEETHEART_WIDTH = 190
export const SWEETHEART_HEIGHT = 96
/** The short side of a banquet table; same furniture as the sweetheart top table. */
export const RECT_DEPTH = 96
/** 4 * SEAT_PITCH, so the shortest rectangle still reads as a rectangle. */
export const MIN_RECT_LENGTH = 184
/** Below this the ends stay bare — see `rectEdgeCounts`. */
export const RECT_END_SEATS_FROM = 8

export const MIN_SEATS = 1
/** The global ceiling; what each shape allows is `MAX_SEATS_BY_SHAPE`. */
export const MAX_SEATS = 20

/**
 * Name plates.
 *
 * A chair can be drawn as an outward-pointing plate carrying a guest's whole
 * name, the way a printed venue chart does it. The plate is a *rendering* of the
 * seat — derived from the same (x, y, facing) — so it moves no chairs and, in
 * particular, does not widen `halfExtent`. That stays the furniture footprint,
 * because the import collision solver and the camera depend on it and neither
 * has any business tracking a PDF display setting.
 */
export const SEAT_PLATE_LENGTH = 76
/**
 * Tangential. Not derived from SEAT_PITCH: a plate reaches *inward* to the
 * chair's inner edge, and on a 13-seat round table the chord there is only
 * 2 * (96.1 - 19) * sin(pi/13) ≈ 36.9. A plate sized off the pitch collides.
 */
export const SEAT_PLATE_WIDTH = 34
/**
 * How much further out a plate reaches than the disc it replaces — the padding
 * a page needs so plates are not clipped at its edge.
 */
export const SEAT_PLATE_REACH = SEAT_PLATE_LENGTH - 2 * SEAT_RADIUS

/**
 * The room.
 *
 * The canvas is a finite hall, not an unbounded plane. Sized in the ratio of the
 * A4-landscape floor page, and **centred on the origin** because every plan ever
 * saved clusters around 0,0 — an off-origin room would displace all of them.
 */
export const ROOM_WIDTH = 4200
export const ROOM_HEIGHT = 2970

/**
 * A round table's chairs crowd as you add them, so it stops at 13. A rectangle
 * only gets longer, so it doesn't have to.
 */
export const MAX_SEATS_BY_SHAPE: Record<TableShape, number> = {
  round: 13,
  square: 13,
  rectangle: 20,
  sweetheart: 13,
}

export const DEFAULT_SEAT_COUNTS: Record<TableShape, number> = {
  round: 10,
  square: 8,
  rectangle: 12,
  sweetheart: 2,
}

export interface SeatPoint {
  index: number
  x: number
  y: number
  /** Degrees clockwise from "up". */
  facing: number
}

/** A chair drawn as an outward name plate. Long axis runs along `facing`. */
export interface SeatPlate {
  x: number
  y: number
  /** Radial. */
  length: number
  /** Tangential. */
  width: number
  facing: number
}

export interface TableGeometry {
  shape: TableShape
  seatCount: number
  width: number
  height: number
  radius: number
  seats: SeatPoint[]
}

type Edge = 'top' | 'right' | 'bottom' | 'left'

/** Chairs fill these edges in this order... */
const ALLOC_ORDER: Edge[] = ['top', 'bottom', 'left', 'right']
/** ...but are emitted clockwise from the top-left, so numbering reads naturally. */
const EMIT_ORDER: Edge[] = ['top', 'right', 'bottom', 'left']
const EDGE_FACING: Record<Edge, number> = { top: 0, right: 90, bottom: 180, left: 270 }

export function maxSeatCount(shape: TableShape): number {
  return MAX_SEATS_BY_SHAPE[shape] ?? 13
}

/**
 * Clamp to what *this* shape allows. The shape is required rather than
 * optional: the cap stopped being one number when the rectangle arrived, and a
 * call site that forgets which table it is talking about would silently let 20
 * chairs onto a round one.
 */
export function clampSeatCount(value: number, shape: TableShape): number {
  return Math.max(MIN_SEATS, Math.min(maxSeatCount(shape), Math.round(value)))
}

export function defaultSeatCount(shape: TableShape): number {
  return DEFAULT_SEAT_COUNTS[shape] ?? 10
}

/**
 * Smallest radius keeping adjacent chairs >= SEAT_PITCH apart. Chairs sit on a
 * ring of radius `r + GAP + SEAT_RADIUS`; the chord between neighbours is
 * `2 * ring * sin(pi / n)`.
 */
function roundRadius(n: number): number {
  if (n <= 1) return MIN_ROUND_RADIUS
  const requiredRing = SEAT_PITCH / (2 * Math.sin(Math.PI / n))
  return Math.max(MIN_ROUND_RADIUS, requiredRing - SEAT_GAP - SEAT_RADIUS)
}

function roundSeats(n: number, radius: number): SeatPoint[] {
  const ring = radius + SEAT_GAP + SEAT_RADIUS
  const seats: SeatPoint[] = []
  for (let i = 0; i < n; i += 1) {
    // Start at 12 o'clock and run clockwise.
    const theta = -Math.PI / 2 + (2 * Math.PI * i) / n
    seats.push({
      index: i,
      x: ring * Math.cos(theta),
      y: ring * Math.sin(theta),
      facing: mod360((theta * 180) / Math.PI + 90),
    })
  }
  return seats
}

function allocateEdges(n: number): Record<Edge, number> {
  const base = Math.floor(n / 4)
  const remainder = n % 4
  const counts = { top: base, right: base, bottom: base, left: base } as Record<Edge, number>
  for (let i = 0; i < remainder; i += 1) counts[ALLOC_ORDER[i]] += 1
  return counts
}

/**
 * `spread` distributes chairs evenly across the whole edge (the banquet look for
 * square tables); `pitch` keeps a fixed distance, centred — which is what the
 * couple's sweetheart table wants so they sit beside each other.
 */
function edgeSeats(
  edge: Edge,
  count: number,
  width: number,
  height: number,
  startIndex: number,
  mode: 'spread' | 'pitch',
): SeatPoint[] {
  if (count <= 0) return []
  const outX = width / 2 + SEAT_GAP + SEAT_RADIUS
  const outY = height / 2 + SEAT_GAP + SEAT_RADIUS
  const span = edge === 'top' || edge === 'bottom' ? width : height
  const seats: SeatPoint[] = []

  for (let j = 0; j < count; j += 1) {
    const offset =
      mode === 'pitch'
        ? (j - (count - 1) / 2) * SEAT_PITCH
        : span * ((j + 0.5) / count) - span / 2

    let x: number
    let y: number
    if (edge === 'top') {
      x = offset
      y = -outY
    } else if (edge === 'bottom') {
      x = -offset // right-to-left, keeping the overall order clockwise
      y = outY
    } else if (edge === 'right') {
      x = outX
      y = offset
    } else {
      x = -outX
      y = -offset // bottom-to-top, completing the clockwise loop
    }
    seats.push({ index: startIndex + j, x, y, facing: EDGE_FACING[edge] })
  }
  return seats
}

function squareSide(n: number): number {
  const counts = allocateEdges(n)
  const busiest = Math.max(...EMIT_ORDER.map((edge) => counts[edge]))
  return Math.max(MIN_SQUARE_SIDE, busiest * SEAT_PITCH)
}

/**
 * Split n chairs the way a banquet table is actually laid: sides first.
 *
 * `allocateEdges` spreads evenly over four edges, which is right for a square
 * and wrong here — a long table seats its guests down the two long sides. The
 * two ends take one chair each, and only once the table is long enough to seat
 * three a side: below that it is too short to seat its ends.
 */
function rectEdgeCounts(n: number): Record<Edge, number> {
  const ends = n >= RECT_END_SEATS_FROM ? 2 : 0
  const along = n - ends
  const top = Math.ceil(along / 2) // the odd chair goes to the top edge, as elsewhere
  return { top, bottom: along - top, left: ends / 2, right: ends / 2 }
}

function rectLength(n: number): number {
  const counts = rectEdgeCounts(n)
  return Math.max(MIN_RECT_LENGTH, Math.max(counts.top, counts.bottom) * SEAT_PITCH)
}

/** Place a per-edge allocation, emitted clockwise from the top-left. */
function rectSeats(
  counts: Record<Edge, number>,
  width: number,
  height: number,
  mode: 'spread' | 'pitch' = 'spread',
): SeatPoint[] {
  const seats: SeatPoint[] = []
  for (const edge of EMIT_ORDER) {
    if (counts[edge] <= 0) continue
    seats.push(...edgeSeats(edge, counts[edge], width, height, seats.length, mode))
  }
  return seats
}

/** The single source of truth for how a table and its chairs are laid out. */
export function computeTableGeometry(shape: TableShape, seatCount: number): TableGeometry {
  const n = clampSeatCount(seatCount, shape)

  if (shape === 'round') {
    const radius = roundRadius(n)
    return {
      shape,
      seatCount: n,
      width: radius * 2,
      height: radius * 2,
      radius,
      seats: roundSeats(n, radius),
    }
  }

  if (shape === 'square') {
    const side = squareSide(n)
    return {
      shape,
      seatCount: n,
      width: side,
      height: side,
      radius: side / 2,
      seats: rectSeats(allocateEdges(n), side, side),
    }
  }

  if (shape === 'rectangle') {
    // The long banquet table: depth is fixed furniture, length grows with the
    // guest list, so adding a chair never crowds the ones already there.
    const length = rectLength(n)
    return {
      shape,
      seatCount: n,
      width: length,
      height: RECT_DEPTH,
      radius: length / 2,
      seats: rectSeats(rectEdgeCounts(n), length, RECT_DEPTH),
    }
  }

  // Sweetheart: the couple's small top table. Both chairs sit on the far edge so
  // the pair faces the room rather than each other.
  const width = Math.max(SWEETHEART_WIDTH, n * SEAT_PITCH + 44)
  const height = SWEETHEART_HEIGHT
  return {
    shape,
    seatCount: n,
    width,
    height,
    radius: width / 2,
    seats: rectSeats({ top: n, right: 0, bottom: 0, left: 0 }, width, height, 'pitch'),
  }
}

export function mod360(degrees: number): number {
  return ((degrees % 360) + 360) % 360
}

export function rotatePoint(x: number, y: number, degrees: number): { x: number; y: number } {
  if (!degrees) return { x, y }
  const rad = (degrees * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return { x: x * cos - y * sin, y: x * sin + y * cos }
}

/** Chair placements in canvas coordinates, with the table's rotation applied. */
export function seatWorldPositions(
  shape: TableShape,
  seatCount: number,
  centerX: number,
  centerY: number,
  rotation = 0,
): SeatPoint[] {
  return computeTableGeometry(shape, seatCount).seats.map((seat) => {
    const rotated = rotatePoint(seat.x, seat.y, rotation)
    return {
      index: seat.index,
      x: centerX + rotated.x,
      y: centerY + rotated.y,
      facing: mod360(seat.facing + rotation),
    }
  })
}

/** Half-width/half-height of a table *including* its chairs, unrotated. */
export function halfExtent(geometry: TableGeometry): { x: number; y: number } {
  const reach = SEAT_GAP + 2 * SEAT_RADIUS
  if (geometry.shape === 'sweetheart') {
    return { x: geometry.width / 2, y: geometry.height / 2 + reach }
  }
  return { x: geometry.width / 2 + reach, y: geometry.height / 2 + reach }
}

export interface Bounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/**
 * The name plate for one chair, in the same coordinates as the chair.
 *
 * Its inner edge sits on the chair's inner edge and it runs outward from there,
 * so the plate covers the disc and reaches `SEAT_PLATE_REACH` past it.
 */
export function seatPlate(point: SeatPoint): SeatPlate {
  const rad = (point.facing * Math.PI) / 180
  const offset = SEAT_PLATE_LENGTH / 2 - SEAT_RADIUS
  return {
    // Outward unit vector for `facing`: 0 degrees is "up", which is -y.
    x: point.x + Math.sin(rad) * offset,
    y: point.y - Math.cos(rad) * offset,
    length: SEAT_PLATE_LENGTH,
    width: SEAT_PLATE_WIDTH,
    facing: point.facing,
  }
}

/** Half-extent of the table's axis-aligned box once it has been turned. */
export function rotatedHalfExtent(
  shape: TableShape,
  seatCount: number,
  rotation = 0,
): { x: number; y: number } {
  const half = halfExtent(computeTableGeometry(shape, seatCount))
  const rad = (rotation * Math.PI) / 180
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))
  return { x: half.x * cos + half.y * sin, y: half.x * sin + half.y * cos }
}

/** Axis-aligned bounds including chairs. */
export function tableBounds(
  shape: TableShape,
  seatCount: number,
  centerX: number,
  centerY: number,
  rotation = 0,
): Bounds {
  const half = rotatedHalfExtent(shape, seatCount, rotation)
  return {
    minX: centerX - half.x,
    minY: centerY - half.y,
    maxX: centerX + half.x,
    maxY: centerY + half.y,
  }
}

export const ROOM_BOUNDS: Bounds = {
  minX: -ROOM_WIDTH / 2,
  minY: -ROOM_HEIGHT / 2,
  maxX: ROOM_WIDTH / 2,
  maxY: ROOM_HEIGHT / 2,
}

/**
 * Nearest centre at which the whole table — chairs included — is in the room.
 *
 * A table larger than the room is centred rather than pinned to a corner, so
 * the degenerate case still looks deliberate.
 */
export function clampTableCenter(
  shape: TableShape,
  seatCount: number,
  centerX: number,
  centerY: number,
  rotation = 0,
  bounds: Bounds = ROOM_BOUNDS,
): { x: number; y: number } {
  const half = rotatedHalfExtent(shape, seatCount, rotation)
  const lowX = bounds.minX + half.x
  const highX = bounds.maxX - half.x
  const lowY = bounds.minY + half.y
  const highY = bounds.maxY - half.y
  return {
    x: lowX > highX ? (bounds.minX + bounds.maxX) / 2 : Math.min(Math.max(centerX, lowX), highX),
    y: lowY > highY ? (bounds.minY + bounds.maxY) / 2 : Math.min(Math.max(centerY, lowY), highY),
  }
}

export function unionBounds(all: Bounds[]): Bounds | null {
  if (all.length === 0) return null
  return {
    minX: Math.min(...all.map((b) => b.minX)),
    minY: Math.min(...all.map((b) => b.minY)),
    maxX: Math.max(...all.map((b) => b.maxX)),
    maxY: Math.max(...all.map((b) => b.maxY)),
  }
}
